import { createElement as h, useEffect, useState, type ReactNode } from "react";
import type {
  GroupMaterialParams,
  MaterialExtensionConfig,
  PickerSettings,
  PivotEditSettings,
} from "../../../shared/api-contract";
import {
  matGemColorHex,
  matGemOpacity,
  matGemUniform,
  matGemVec3,
  matMetalColor,
  matMetalParam,
} from "../inspector/bindings";
import type { BindingCtx, DomainRead } from "../../../state/editor/types";
import type { InertTag, RowDef, SectionDef } from "../inspector/schema";
import { CompactCheck, CompactColor, CompactNumber, CompactSlider, Row } from "../inspector/rows";
import { WorkspacePresets } from "./custom/WorkspacePresets";
import { NodeGeometry, NodeInspector } from "./custom/NodeInspector";
import { useDomainState } from "../../../state/editor/useEditor";
import { FullMaterialEditor } from "./custom/FullMaterialEditor";
import { GemInclusionsControl } from "./custom/GemInclusions";
import { HierarchyTree } from "./custom/HierarchyTree";
import { MaterialLibrary } from "./custom/MaterialLibrary";
import { TransformControl } from "./custom/TransformControl";
import { DiamondsControl } from "./custom/DiamondsControl";
import { PivotControlsControl } from "./custom/PivotControlsControl";
import { MaterialMapsControl } from "./custom/MaterialMapsControl";
import { MaterialExtensionsControl } from "./custom/MaterialExtensionsControl";
import { XAtlasControl, WidgetsControl } from "./custom/NichePluginControls";

/**
 * Picking tab — Hierarchy + Material library + the full per-group material
 * editor (metal PBR params / diamond ray-trace uniforms) + Picker stub.
 * Transcribed from RingSidebar + MaterialEditor. The two material sections only
 * render their rows for the matching material kind; when nothing is selected
 * they show a "select a part" note. Ranges/steps come straight from
 * MaterialEditor.tsx (gem uniforms use very fine 0.00001 steps).
 */

const mat = (read: DomainRead): GroupMaterialParams | null =>
  read("material") as GroupMaterialParams | null;

const isMetal = (read: DomainRead) => mat(read)?.kind === "metal";
const isGem = (read: DomainRead) => mat(read)?.kind === "gem";
const noSelection = (read: DomainRead) => mat(read) == null;

/** `Row` requires `children` in props, so pass it explicitly (createElement's
 * rest-args don't satisfy the required-children prop type). */
const row = (label: string, child: ReactNode, inert?: InertTag) => h(Row, { label, inert, children: child });

const NO_SELECTION_NOTE: RowDef = {
  id: "noSelection",
  kind: "note",
  label: "",
  text: "Select a part to edit its material.",
  visible: (read) => noSelection(read),
};

/**
 * Picker (checklist title verbatim). Outline moved out to its own section in
 * the Post Processing tab (`postfx.ts`) — this only carries the picker-proper
 * fields, unlike the old merged "Picker & Outline" panel.
 */
function PickerRow({ ctx }: { ctx: BindingCtx; read: DomainRead }) {
  const r = ctx.ring;
  const [p, setP] = useState<PickerSettings | null>(null);
  useEffect(() => {
    r.getPickerSettings().then(setP).catch(() => {});
  }, [r]);
  if (!p) return null;
  const set = (patch: Partial<PickerSettings>) => r.setPickerSettings(patch).then(setP);
  return h(
    "div",
    { className: "flex flex-col" },
    row("Picking", h(CompactCheck, { checked: p.enabled, onChange: (v: boolean) => void set({ enabled: v }) })),
    row("Auto Focus", h(CompactCheck, { checked: p.autoFocus, onChange: (v: boolean) => void set({ autoFocus: v }) })),
    row("Multi-Select", h(CompactCheck, { checked: p.multiSelect, onChange: (v: boolean) => void set({ multiSelect: v }) })),
    row("Hover Highlight", h(CompactCheck, { checked: p.hover, onChange: (v: boolean) => void set({ hover: v }) })),
  );
}

/**
 * Pivot Edit (checklist verbatim title, v19 `PivotEditSettings`). Previously
 * only a sub-header inside the Pivot Controls custom control with no Enabled
 * row; this stands alone and carries the full field set including the
 * previously-missing `enabled` toggle.
 */
function PivotEditRow({ ctx }: { ctx: BindingCtx; read: DomainRead }) {
  const r = ctx.ring;
  const [p, setP] = useState<PivotEditSettings | null>(null);
  useEffect(() => {
    r.getPivotEdit().then(setP).catch(() => {});
  }, [r]);
  if (!p) return null;
  const set = (patch: Partial<PivotEditSettings>) => r.setPivotEdit(patch).then(setP);
  return h(
    "div",
    { className: "flex flex-col" },
    row("Enabled", h(CompactCheck, { checked: p.enabled, onChange: (v: boolean) => void set({ enabled: v }) })),
    row("Edit Pivot", h(CompactCheck, { checked: p.editPivot, onChange: (v: boolean) => void set({ editPivot: v }) })),
    row("Show Pivot Marker", h(CompactCheck, { checked: p.showPivotMarker, onChange: (v: boolean) => void set({ showPivotMarker: v }) })),
    row("Marker Scale", h(CompactSlider, { value: p.markerScale, min: 0.1, max: 5, step: 0.01, onChange: (v: number) => void set({ markerScale: v }) })),
    row("Marker Color", h(CompactColor, { value: p.markerColor, onChange: (v: string) => void set({ markerColor: v }) })),
  );
}

/** Replace Material (checklist field, `getMaterialLibraryReplace`/
 * `setMaterialLibraryReplace` — stored, unused by any UI until now). webgi:
 * false (default) copies preset params onto the existing material instance;
 * true swaps the instance. */
function ReplaceMaterialRow({ ctx }: { ctx: BindingCtx; read: DomainRead }) {
  const r = ctx.ring;
  const [v, setV] = useState(false);
  useEffect(() => {
    r.getMaterialLibraryReplace().then(setV).catch(() => {});
  }, [r]);
  return row(
    "Replace Material",
    h(CompactCheck, {
      checked: v,
      onChange: (next: boolean) => {
        setV(next);
        void r.setMaterialLibraryReplace(next);
      },
    }),
    {
      reason:
        "Stored, never read. `_materialLibraryReplace` has no consumer beyond its own getter/setter \u2014 applying a preset always copies params onto the existing material instance, whatever this says.",
    },
  );
}

const DEFAULT_LAYERED: Pick<
  MaterialExtensionConfig,
  "layeredEnabled" | "layeredCutoff" | "layeredCavityColor" | "layeredCavityRoughness" | "layeredCavityMetalness" | "layeredCavityScale" | "layeredStepSize"
> = {
  layeredEnabled: false,
  layeredCutoff: -0.65,
  layeredCavityColor: "#585757",
  layeredCavityRoughness: 0.5,
  layeredCavityMetalness: 0.5,
  layeredCavityScale: 1,
  layeredStepSize: 5,
};

/**
 * LayeredMaterial cavity layer (webgi `WEBGI_materials_layered`, v32) — a
 * screen-space curvature "worn crevice / patina" darkening, metal-only. Rides
 * the existing get/setGroupMaterialExtensions (not a new method name), so no
 * caps gate is required; legacy viewers ignore the unknown patch fields.
 */
function LayeredMaterialRow({ ctx, read }: { ctx: BindingCtx; read: DomainRead }) {
  const groupId = useDomainState("selection") as string | null;
  const [p, setP] = useState(DEFAULT_LAYERED);
  useEffect(() => {
    if (!groupId) return;
    ctx.ring.getGroupMaterialExtensions(groupId).then((c) => { if (c) setP({ ...DEFAULT_LAYERED, ...c }); }).catch(() => {});
  }, [groupId, ctx.ring]);
  if (!isMetal(read) || !groupId) return null;
  const set = (patch: Partial<MaterialExtensionConfig>) => {
    setP((prev) => ({ ...prev, ...patch }));
    void ctx.ring.setGroupMaterialExtensions(groupId, patch);
  };
  return h(
    "div",
    { className: "flex flex-col" },
    h("div", { className: "mt-1 px-3 py-1 text-[12px] font-semibold text-ed-text-2" }, "Layered Material (cavity)"),
    row("Enabled", h(CompactCheck, { checked: p.layeredEnabled, onChange: (v: boolean) => set({ layeredEnabled: v }) })),
    row("Cutoff", h(CompactSlider, { value: p.layeredCutoff, min: -2, max: 2, step: 0.01, onChange: (v: number) => set({ layeredCutoff: v }) })),
    row("Cavity Color", h(CompactColor, { value: p.layeredCavityColor, onChange: (v: string) => set({ layeredCavityColor: v }) })),
    row("Cavity Roughness", h(CompactSlider, { value: p.layeredCavityRoughness, min: 0, max: 1, step: 0.01, onChange: (v: number) => set({ layeredCavityRoughness: v }) })),
    row("Cavity Metalness", h(CompactSlider, { value: p.layeredCavityMetalness, min: 0, max: 1, step: 0.01, onChange: (v: number) => set({ layeredCavityMetalness: v }) })),
    row("Cavity Scale", h(CompactSlider, { value: p.layeredCavityScale, min: 0, max: 4, step: 0.01, onChange: (v: number) => set({ layeredCavityScale: v }) })),
    row("Step Size", h(CompactNumber, { value: p.layeredStepSize, min: 0, max: 16, step: 1, width: 64, onChange: (v: number) => set({ layeredStepSize: v }) })),
  );
}

const PICKING_SECTIONS: SectionDef[] = [
  {
    id: "picker",
    title: "Picker",
    rows: [{ id: "pickerControl", kind: "custom", label: "", component: PickerRow }],
  },
  {
    id: "transform",
    title: "Transform Controls",
    defaultCollapsed: true,
    rows: [
      { id: "transformControl", kind: "custom", label: "", component: TransformControl },
    ],
  },
  {
    id: "pivotControls",
    title: "Pivot Controls",
    defaultCollapsed: true,
    rows: [{ id: "pivot", kind: "custom", label: "", component: PivotControlsControl }],
  },
  {
    id: "pivotEdit",
    title: "Pivot Edit",
    defaultCollapsed: true,
    rows: [{ id: "pivotEdit", kind: "custom", label: "", component: PivotEditRow }],
  },
  {
    id: "uvUnwrap",
    title: "UV Unwrapping (xAtlas)",
    defaultCollapsed: true,
    inert: {
      label: "best-effort",
      reason:
        "Runs, but it is a built-in box-projection stand-in, not xAtlas chart packing (that needs a wasm dep). Nothing in the jewelry pipeline consumes uv2, so the generated channel has no reader.",
    },
    rows: [{ id: "xatlas", kind: "custom", label: "", component: XAtlasControl }],
  },
  {
    id: "widgets",
    title: "Widgets",
    defaultCollapsed: true,
    rows: [{ id: "widgets", kind: "custom", label: "", component: WidgetsControl }],
  },
  {
    id: "materialLibrary",
    title: "Material Library",
    info:
      "Applies a whole material to the selected part. The same presets are on the left rail, split by category, whenever something is selected.",
    domains: ["groups", "selection"],
    rows: [
      { id: "library", kind: "custom", label: "", domains: ["groups", "selection", "material"], component: MaterialLibrary },
      { id: "replaceMaterial", kind: "custom", label: "", keywords: "Replace Material", component: ReplaceMaterialRow },
    ],
  },
  {
    // Ported from the Canvas chrome, which was the only surface that could
    // WRITE a workspace preset — and the only one with the fan-out that makes a
    // saved metal useful on a multi-part model. Preset documents of kind
    // metal/gem already exist in workspaces that used it.
    id: "savedMaterials",
    title: "Saved Materials",
    info:
      "Your workspace's own materials. Saving captures the selected part as it looks now; applying with nothing of that kind selected fans out to EVERY part of that kind.",
    defaultCollapsed: true,
    domains: ["groups", "selection", "material"],
    rows: [
      {
        id: "savedMetal",
        kind: "custom",
        label: "",
        keywords: "Save as metal preset",
        component: () => h(WorkspacePresets, { kind: "metal" }),
      },
      {
        id: "savedGem",
        kind: "custom",
        label: "",
        keywords: "Save as stone preset",
        component: () => h(WorkspacePresets, { kind: "gem" }),
      },
    ],
  },
  {
    id: "diamonds",
    title: "Diamonds",
    defaultCollapsed: true,
    rows: [{ id: "diamonds", kind: "custom", label: "", component: DiamondsControl }],
  },
  {
    id: "material",
    title: "Material",
    domains: ["material", "selection"],
    rows: [
      NO_SELECTION_NOTE,
      { id: "color", kind: "color", label: "Colour", visible: isMetal, binding: matMetalColor() },
      { id: "roughness", kind: "slider", label: "Roughness", min: 0, max: 1, step: 0.01, visible: isMetal, binding: matMetalParam("roughness") },
      { id: "metalness", kind: "slider", label: "Metalness", min: 0, max: 1, step: 0.01, visible: isMetal, binding: matMetalParam("metalness") },
      { id: "envMapIntensity", kind: "slider", label: "Env Intensity", min: 0, max: 5, step: 0.01, visible: isMetal, binding: matMetalParam("envMapIntensity") },
      { id: "ior", kind: "slider", label: "IOR", min: 1.0, max: 2.5, step: 0.01, visible: isMetal, binding: matMetalParam("ior") },
      { id: "clearcoat", kind: "slider", label: "Clearcoat", min: 0, max: 1, step: 0.01, visible: isMetal, binding: matMetalParam("clearcoat") },
      { id: "clearcoatRoughness", kind: "slider", label: "Clearcoat Roughness", min: 0, max: 1, step: 0.01, visible: isMetal, binding: matMetalParam("clearcoatRoughness") },
      { id: "specularIntensity", kind: "slider", label: "Specular Intensity", min: 0, max: 2, step: 0.01, visible: isMetal, binding: matMetalParam("specularIntensity") },
      { id: "sheen", kind: "slider", label: "Sheen", min: 0, max: 1, step: 0.01, visible: isMetal, binding: matMetalParam("sheen") },
      { id: "anisotropy", kind: "slider", label: "Anisotropy", min: 0, max: 1, step: 0.01, visible: isMetal, binding: matMetalParam("anisotropy") },
    ],
  },
  {
    id: "diamondRayTracing",
    title: "Diamond / Gem Material",
    defaultCollapsed: true,
    domains: ["material", "selection"],
    rows: [
      NO_SELECTION_NOTE,
      { id: "color", kind: "color", label: "Color", visible: isGem, binding: matGemColorHex() },
      { id: "bounces", kind: "slider", label: "Bounces", min: 1, max: 16, step: 1, visible: isGem, binding: matGemUniform("bounces") },
      { id: "ior", kind: "slider", label: "IOR", min: 1.0, max: 3.0, step: 0.00001, visible: isGem, binding: matGemUniform("ior") },
      { id: "dispersion", kind: "slider", label: "Dispersion", min: 0, max: 0.15, step: 0.00001, visible: isGem, binding: matGemUniform("dispersion") },
      { id: "reflectivity", kind: "slider", label: "Reflectivity", min: 0, max: 1, step: 0.00001, visible: isGem, binding: matGemUniform("reflectivity") },
      { id: "envMapIntensity", kind: "slider", label: "Env Intensity", min: 0, max: 5, step: 0.00001, visible: isGem, binding: matGemUniform("envMapIntensity") },
      { id: "sparkleBoost", kind: "slider", label: "Sparkle Boost", min: 0.5, max: 2.0, step: 0.00001, visible: isGem, binding: matGemUniform("sparkleBoost") },
      { id: "absorptionFactor", kind: "slider", label: "Absorption", min: 0, max: 3, step: 0.00001, visible: isGem, binding: matGemUniform("absorptionFactor") },
      { id: "gammaFactor", kind: "slider", label: "Gamma", min: 0.1, max: 3.0, step: 0.00001, visible: isGem, binding: matGemUniform("gammaFactor") },
      { id: "squashFactor", kind: "slider", label: "Squash", min: 0.1, max: 2.0, step: 0.00001, visible: isGem, binding: matGemUniform("squashFactor") },
      { id: "geometryFactor", kind: "slider", label: "Geometry", min: 0, max: 1, step: 0.00001, visible: isGem, binding: matGemUniform("geometryFactor") },
      // Overall diamond reflectivity (v30; 1 = unchanged).
      { id: "surfaceReflBoost", kind: "slider", label: "Surface Reflection", min: 0, max: 4, step: 0.01, visible: isGem, binding: matGemUniform("surfaceReflBoost") },
      { id: "internalEnvBoost", kind: "slider", label: "Inner Fire", min: 0, max: 4, step: 0.01, visible: isGem, binding: matGemUniform("internalEnvBoost") },
      { id: "envRotation", kind: "slider", label: "Env Rotation", min: 0, max: 6.28318, step: 0.00001, visible: isGem, binding: matGemUniform("envRotation") },
      { id: "boostR", kind: "slider", label: "Boost R", min: 0, max: 4, step: 0.00001, visible: isGem, binding: matGemVec3("boostFactors", "x") },
      { id: "boostG", kind: "slider", label: "Boost G", min: 0, max: 4, step: 0.00001, visible: isGem, binding: matGemVec3("boostFactors", "y") },
      { id: "boostB", kind: "slider", label: "Boost B", min: 0, max: 4, step: 0.00001, visible: isGem, binding: matGemVec3("boostFactors", "z") },
      { id: "colorR", kind: "slider", label: "Color R", min: 0, max: 2, step: 0.00001, visible: isGem, binding: matGemVec3("color", "x") },
      { id: "colorG", kind: "slider", label: "Color G", min: 0, max: 2, step: 0.00001, visible: isGem, binding: matGemVec3("color", "y") },
      { id: "colorB", kind: "slider", label: "Color B", min: 0, max: 2, step: 0.00001, visible: isGem, binding: matGemVec3("color", "z") },
      { id: "opacity", kind: "slider", label: "Opacity", min: 0, max: 1, step: 0.00001, visible: isGem, binding: matGemOpacity() },
      // Gem inclusions: iJewel's "Inclusions" panel (webgi GemRefraction, v62)
      // and, under it, our v32 speckle layer. caps.v32-gated; the webgi rows
      // need a v62 viewer (sections/custom/GemInclusions.tsx).
      {
        id: "inclusions",
        kind: "custom",
        label: "",
        keywords: "Gem Inclusions Inclusion Map Speckle Bump Scale Tint Cloud Crystal Feather Needle Pinpoint flaw webgi GemRefraction milky blur roughness normal map tourmaline ametrine",
        component: GemInclusionsControl,
      },
    ],
  },
  {
    // The reference appends a section NAMED AFTER THE MESH here, then Geometry,
    // the moment something is selected — below Widgets, above the material
    // block. `visible` gates on selection only: BindingCtx carries no caps, so
    // the version gate lives inside the component, which is how the rest of
    // this editor does it (see OnBodyPanel's v49 note).
    id: "nodeInspector",
    title: "Node",
    domains: ["selection", "groups"],
    visible: (read) => !!read("selection"),
    info:
      "The selected node itself — where it sits, how it is rotated and scaled, and how it takes part in shadows and culling. Delete, Fit and Mirror live on the toolbar; the pivot gizmo lives in Transform Controls.",
    rows: [{ id: "node", kind: "custom", label: "", component: NodeInspector }],
  },
  {
    id: "nodeGeometry",
    title: "Geometry",
    defaultCollapsed: true,
    domains: ["selection", "groups"],
    visible: (read) => !!read("selection"),
    info:
      "Counts for the selected node's whole subtree. Draw calls are not shown: the renderer reports those per frame for the whole scene, so there is no honest per-node number.",
    rows: [{ id: "geom", kind: "custom", label: "", component: NodeGeometry }],
  },
  {
    // ⚠️ Two names for one section, and both are load-bearing.
    //
    // `title` is the captured checklist's spelling, which
    // scripts/check-ijewel-parity.mjs matches by EXACT equality — change it and
    // the guard fails. `displayTitle` is what the live reference shows today
    // (measured 2026-09-12). The capture and the live app disagree, most likely
    // across builds; keeping both means the guard still does its job AND the UI
    // reads the way the reference does.
    id: "materialAdvanced",
    title: "Shared PBR Material Editor",
    displayTitle: "Physical Material",
    info:
      "Every physical-material field for the selected part: colour, roughness and metalness, clearcoat, sheen, iridescence, refraction and the rest. The Material section above is the short list.",
    defaultCollapsed: true,
    domains: ["material", "selection"],
    rows: [{ id: "fullMat", kind: "custom", label: "", component: FullMaterialEditor }],
  },
  {
    id: "materialMaps",
    title: "Texture Maps",
    defaultCollapsed: true,
    domains: ["selection"],
    rows: [{ id: "maps", kind: "custom", label: "", domains: ["selection"], component: MaterialMapsControl }],
  },
  {
    id: "materialExtensions",
    title: "Material Extensions",
    defaultCollapsed: true,
    domains: ["selection"],
    rows: [
      { id: "ext", kind: "custom", label: "", domains: ["selection"], component: MaterialExtensionsControl },
      // LayeredMaterial cavity layer (webgi WEBGI_materials_layered, v32).
      { id: "layered", kind: "custom", label: "", keywords: "Layered Material Cavity Layer", domains: ["selection"], component: LayeredMaterialRow },
    ],
  },
  // Hierarchy is an OF addition in NAME (no iJewel checklist section), but the
  // reference has the same thing and puts it FIRST — see REFERENCE_ORDER below,
  // which is what decides where this lands. Open by default: a part list you
  // have to expand before you can pick anything is a part list nobody finds.
  {
    id: "hierarchy",
    title: "Hierarchy",
    info:
      "Every node in the loaded scene. Click one to select it — which is also what makes the material panels appear on the left rail. The eye hides a node without deleting it.",
    domains: ["groups", "selection"],
    rows: [{ id: "tree", kind: "custom", label: "", domains: ["groups", "selection"], component: HierarchyTree }],
  },
];

/**
 * Tab order, measured live off the reference on 2026-09-12.
 *
 * Hierarchy FIRST and Material Library SECOND is the whole point: ours had
 * Hierarchy fourteenth and collapsed, so the only ergonomic way to select a
 * part was clicking the mesh in the 3D viewport, and the material presets sat
 * seventh behind it.
 *
 * Declared as a list rather than by reshuffling five hundred lines of section
 * bodies: the order IS the thing being asserted against the reference, and it
 * should be readable in one glance and diffable in one line.
 *
 * ⚠️ These are `id`s, not titles. Renaming one fails silently — `order()` drops
 * an id it cannot find rather than throwing, because a section legitimately
 * disappears behind a caps gate. The verify:layout guard is what catches a typo.
 */
const REFERENCE_ORDER = [
  "hierarchy",
  "materialLibrary",
  "savedMaterials",
  "diamonds",
  "transform",
  "pivotControls",
  "pivotEdit",
  "picker",
  "uvUnwrap",
  "widgets",
  // The reference grows its per-mesh / Geometry / Physical Material block here,
  // below Widgets, once something is selected. Ours are the equivalent.
  "material",
  // v58: the reference's per-mesh + Geometry pair, which appears only with a
  // selection — same place it sits there, below Widgets.
  "nodeInspector",
  "nodeGeometry",
  "diamondRayTracing",
  "materialAdvanced",
  "materialMaps",
  "materialExtensions",
];

/** Reorder by id; anything not named keeps its relative position at the end, so
 *  a new section is never silently dropped just because nobody listed it. */
function order(sections: SectionDef[], ids: string[]): SectionDef[] {
  const byId = new Map(sections.map((s) => [s.id, s]));
  const named = ids.map((id) => byId.get(id)).filter((s): s is SectionDef => !!s);
  const rest = sections.filter((s) => !ids.includes(s.id));
  return [...named, ...rest];
}

export const pickingSections: SectionDef[] = order(PICKING_SECTIONS, REFERENCE_ORDER);
